/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogProps,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    Field,
    Input,
    makeStyles,
} from '@fluentui/react-components';
import { TagRegular } from '@fluentui/react-icons';
import { useFormik } from 'formik';
import { Column, Row } from '../common';
import { useValidation } from '../../data/types/validation';
import { useDialogControls } from './useDialogControls';

const useStyles = makeStyles({
    root: {
        maxWidth: '400px',
    },
    body: {
        maxWidth: 'inherit',
        width: 'inherit',
        paddingInline: '.5rem',
    },
    input: {
        width: '100%',
    },
});

export type AddCaseTypeDialogProps = Omit<Partial<DialogProps>, 'onOpenChange'> & {
    children?: JSX.Element;
    open?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
    onSubmit?: (caseType: string) => Promise<void> | void;
    existingCaseTypes?: string[];
};
export const AddCaseTypeDialog = (props: AddCaseTypeDialogProps) => {
    const { children, modalType = 'modal', open, onOpenChange, onSubmit, existingCaseTypes = [] } = props;

    //Hooks
    const { validateCaseType } = useValidation();

    //Styles
    const styles = useStyles();

    //Dialog Controls
    const [dialogOpen, onOpenChangeHandler, closeDialog] = useDialogControls(open, onOpenChange);

    //Form
    const caseTypeForm = useFormik({
        initialValues: { type: '' },
        validationSchema: validateCaseType.schema,
        onSubmit: async (values, { resetForm }) => {
            const newCaseType = values.type.trim();

            !!onSubmit && (await onSubmit(newCaseType));

            resetForm();
            closeDialog();
        },
    });

    //Logic
    const trimmedType = caseTypeForm.values.type.trim();
    const isDuplicate = existingCaseTypes.some((caseType) => caseType.toLowerCase() === trimmedType.toLowerCase());
    const canSubmit = !!trimmedType && caseTypeForm.isValid && !isDuplicate;

    //UI Events
    const onKeyUp = (event: any) => {
        if (event.key === 'Enter' && canSubmit) {
            caseTypeForm.submitForm();
        }
    };
    const clickSubmit = (e: any) => {
        e.stopPropagation();
        caseTypeForm.submitForm();
    };
    const clickCancel = (e: any) => {
        e.stopPropagation();
        caseTypeForm.resetForm();
        closeDialog();
    };

    //Effects
    useEffect(() => {
        if (dialogOpen) {
            caseTypeForm.resetForm();
        }
    }, [dialogOpen]);

    return (
        <>
            <Dialog modalType={modalType} open={dialogOpen} onOpenChange={onOpenChangeHandler}>
                {!!children && <DialogTrigger disableButtonEnhancement>{children}</DialogTrigger>}
                <DialogSurface aria-describedby={undefined} mountNode={undefined} className={styles.root}>
                    <DialogBody className={styles.body}>
                        <DialogTitle>
                            <Row gap='medium'>
                                <Column width='auto' vAlign='middle'>
                                    <TagRegular />
                                </Column>
                                <Column>Add Case Type</Column>
                            </Row>
                        </DialogTitle>
                        <DialogContent>
                            <Field
                                label='New Case Type'
                                required
                                validationMessage={
                                    isDuplicate ? 'This case type already exists' : caseTypeForm.errors.type
                                }
                            >
                                <Input
                                    className={styles.input}
                                    name='type'
                                    value={caseTypeForm.values.type}
                                    onChange={caseTypeForm.handleChange}
                                    onKeyUp={onKeyUp}
                                    maxLength={validateCaseType.type.maxLength}
                                    placeholder='Enter the case type'
                                    data-testid='add-case-type-dialog-type-input'
                                />
                            </Field>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                type='submit'
                                appearance='primary'
                                disabled={!canSubmit}
                                onClick={clickSubmit}
                                data-testid='add-case-type-dialog-submit-button'
                            >
                                Submit
                            </Button>
                            <Button
                                appearance='secondary'
                                onClick={clickCancel}
                                data-testid='add-case-type-dialog-cancel-button'
                            >
                                Cancel
                            </Button>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        </>
    );
};
